import type { BaseQueryFn } from "@reduxjs/toolkit/query";
import { createApi } from "@reduxjs/toolkit/query/react";
import type { AxiosError, AxiosRequestConfig, Method } from "axios";
import axios from "axios";

type QueryArg = {
  url: string;
  method: Method;
  data?: AxiosRequestConfig["data"];
  params?: AxiosRequestConfig["params"];
  validateStatus?: AxiosRequestConfig["validateStatus"];
};

const axiosBaseQuery =
  (
    { baseUrl }: { baseUrl: string } = { baseUrl: "" },
  ): BaseQueryFn<QueryArg, unknown, unknown> =>
  async ({ url, method, data, params, ...rest }) => {
    try {
      const result = await axios({
        url: baseUrl + url,
        method,
        data,
        params,
        ...rest,
      });
      return { data: result.data };
    } catch (axiosError) {
      const err = axiosError as AxiosError;

      if (err.response?.status && err.response.status >= 500) {
        const errorData = err.response?.data;
        const parsedMessage =
          typeof errorData === "string"
            ? { _error: ["Something went wrong!"] }
            : errorData;
        return {
          error: {
            status: err.response?.status,
            data: parsedMessage,
          },
        };
      }

      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };

export const API = createApi({
  baseQuery: axiosBaseQuery({ baseUrl: "/api" }),
  endpoints: () => ({}),
  refetchOnMountOrArgChange: 60,
  invalidationBehavior: "delayed",
  tagTypes: [
    "InternalReviews",
    "TalentInviteSuggestions",
    "MessengerRooms",
    "RoomMessages",
    "TalentReviews",
    "TalentReviewsSummary",
    "EmployerReviews",
    "OpenReviewProcesses",
    "FreelancerRoles",
    "FreelancerBids",
    "WalletActivity",
    "SimilarJobs",
    "EmployerBids",
    "EmployerOffers",
    "FreelancerOffers",
    "WalletBalance",
    "Jobs",
    "RelevantJobs",
    "ManagedJobs",
    "JobExternalReposting",
    "SavedJobs",
    "ManagedEmployers",
    "BulkOffersPreview",
    "TokenGrantPlans",
    "FrontendStorage",
    "EmployerOpenJobs",
    "RewardRules",
    "TaxForm",
    "MoneyFlowDashboard",
    "ReferralReport",
    "TalentsWithJobsReport",
    "PaymentMethods",
    "OfferReport",
    "OfferNotes",
    "Notes",
    "Rosters",
    "InvoiceReport",
    "InvoiceReportUnpaidInvoices",
    "InvoiceReportPaymentHistory",
    "FreelancerJobInvitations",
    "EmployerATSIntegrations",
    "FreelancerWorkExperience",
    "JobSubscribers",
    "JobSubscriberInvitations",
    "FreelancerSkills",
    "Skills",
    "JobBidsLocations",
    "JobDrafts",
    "IdentityVerifications",
    "EmployerTeamMembers",
    "EmployerJobsMinimal",
    "FreelancerProfileCompletion",
    "ResumeExtractedData",
    "FreelancerExternalProfiles",
    "EmployerHasExceededDailyJobsLimit",
    "ATSSyncedJobs",
    "ATSHiddenJobs",
    "EmployerInvoicesIds",
    "EmployerInvoicesMinimal",
    "JobOwners",
    "CanWithdrawTokens",
    "BidNotes",
    "EmployerInvoiceStatistics",
    "EmployerInvoices",
    "EmployerInvoiceStatusCounts",
    "EmployerConsolidatedInvoices",
    "EmployerMyTalentStats",
    "EmployerMyTalent",
    "FreelancerJobOffers",
    "FreelancerLocation",
    "FreelancerPublicProfile",
    "ActiveSessions",
    "TokenTransactionsList",
    "InvitationsToManageEmployer",
    "EmployerCurrentOfferDeposit",
    "EmployerPublicProfile",
    "OfferHistory",
    "SpaceEmailInvitations",
    "ExpertContributors",
    "Passkeys",
    "SavedJobFilters",
    "SavedTalentFilters",
    "WithdrawalMethods",
    "ExpertContributorsInvitations",
    "ExpertContributorsApplications",
  ],
});

export const resetGlobalAPIState = () => API.util.resetApiState();
